import React, { Fragment, useCallback } from "react";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { Typography } from "ui-kit/Typography";
import { Radio } from "ui-kit/Radio";
import { FlexRow } from "ui-kit/Layout/Flex";
import ResponsiveGutter from "ui-kit/ResponsiveGutter";
import { Button } from "ui-kit/Button";
import { StepProps } from "../../types";
import { useSignupDispatch } from "components/Signup/store/hooks";
import {
  updateAnimalById,
  updateInsuranceById,
} from "components/Signup/store/slices/animals";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { InsurelyResponseItem } from "components/Signup/store/state/types/insurely";
import { Copay, Deductible, Limit } from "components-shared/insuranceData";
import { Castration, Sex } from "components/Signup/store/state/types";
import {
  getCompetitorName,
  getCompetitorRenewalDate,
  isCancellationAllowedByCompetitor,
} from "components/Signup/components/utils/insurely/insurelyUtil";
import { ssnFormatter } from "utils/validation";
import Gutter from "ui-kit/Gutter";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useWebTranslation } from "translations";
import { SpeciesEnum } from "../../../../../../__generated__/globalTypes";
import { CancellationDisclaimer } from "components/Signup/components/CancellationDisclaimer";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";

const chooseCurrentLimit = (competitorVetCare: number | undefined): Limit => {
  if (!competitorVetCare) {
    return 6000000;
  }
  if (competitorVetCare < 30001) {
    return 3000000;
  }
  if (competitorVetCare > 80000) {
    return 16000000;
  }

  return 6000000;
};

const chooseCurrentDeductible = (
  competitorDeductible: number | undefined
): Deductible => {
  if (!competitorDeductible) {
    return 150000;
  }
  if (competitorDeductible > 2500) {
    return 350000;
  }
  return 150000;
};

const chooseCurrentCopay = (competitorCopay: number | undefined): Copay => {
  if (!competitorCopay) {
    return 0.25;
  }
  if (competitorCopay < 21) {
    return 0.15;
  }
  return 0.25;
};

const getAnimalSex = (item: InsurelyResponseItem): Sex | null => {
  const insurelyGender = item.insurance.animalGender;
  if (insurelyGender?.includes("FEMALE")) {
    return "female";
  }
  if (insurelyGender?.includes("MALE")) {
    return "male";
  }
  return null;
};

const getSexSituation = (item: InsurelyResponseItem): Castration | null => {
  const insurelyGender = item.insurance.animalGender;
  if (insurelyGender?.includes("CASTRATED")) {
    return "castrated";
  }
  if (insurelyGender === "UNKNOWN") {
    return null;
  }
  return "not-castrated";
};

const ChooseAnimal = ({
  next,
  isValid,
  previous,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();
  const dispatch = useSignupDispatch();
  const { id } = useCurrentAnimal();
  const insurance = useCurrentInsuranceByAnimalId(id);
  const isCancellationPossible = isCancellationAllowedByCompetitor(
    insurance.insurely
  );

  const onChangeAnimal = useCallback(
    (index: number) => {
      dispatch(
        updateInsuranceById(
          {
            id: insurance.id,
            update: {
              insurely: {
                ...insurance.insurely,
                currentInsurelyPolicyIndex: index,
              },
            },
          },
          { refetch: false }
        )
      );
    },
    [dispatch, insurance.id, insurance.insurely]
  );

  const onNext = useCallback(() => {
    if (insurance.insurely?.currentInsurelyPolicyIndex === undefined) {
      return;
    }

    const policy =
      insurance.insurely?.policies?.[
        insurance.insurely.currentInsurelyPolicyIndex
      ];

    if (!policy) {
      return;
    }

    dispatch(
      updateInsuranceById(
        {
          id: insurance.id,
          update: {
            limit: chooseCurrentLimit(policy.insurance.veterinaryCareAmount),
            copay: chooseCurrentCopay(
              policy.deductibles?.find(
                (d) => d.deductibleId === "deductibleVariable"
              )?.deductibleAmount
            ),
            deductible: chooseCurrentDeductible(
              policy.deductibles?.find(
                (d) => d.deductibleId === "deductibleFixed"
              )?.deductibleAmount
            ),
            startDate: getCompetitorRenewalDate(insurance.insurely),
            insurely: {
              ...insurance.insurely,
              shouldCompare: true,
            },
          },
        },
        { refetch: false }
      )
    );

    dispatch(
      updateAnimalById({
        id,
        update: {
          name: policy.insurance.animalName ?? "",
          birthday: policy.insurance.dateOfBirth?.substring(0, 10) ?? "",
          sexSituation: getSexSituation(policy),
          sex: getAnimalSex(policy),
          species:
            policy.insurance?.insuranceSubType === "catInsurance"
              ? SpeciesEnum.cat
              : SpeciesEnum.dog,
        },
      })
    );

    dispatch(
      updateCustomer({
        ssn: ssnFormatter(policy.personalInformation.PERSONAL_NUMBER),
      })
    );

    next();
  }, [
    insurance.insurely?.currentInsurelyPolicyIndex,
    insurance.insurely?.policies,
  ]);

  return (
    <>
      <ChatContainer leaving={transitioning}>
        <ChatMessage>
          <Typography>
            {t("competitor.comparison.insurance_found", {
              competitorName: getCompetitorName(insurance.insurely) ?? "?",
            })}
          </Typography>
        </ChatMessage>
        <ChatMessage>
          <Typography>{t("competitor.comparison.insurance_select")}</Typography>
        </ChatMessage>

        <ChatMessage unSent fromMe>
          {insurance.insurely?.policies?.map((item, index) => {
            const label =
              item.insurance.animalName ??
              item.insurance.insuranceName ??
              (item.insurance?.insuranceSubType === "catInsurance"
                ? "Katt"
                : "Hund");
            return (
              <Fragment key={item.insurance.animalName}>
                <Radio
                  active={
                    insurance.insurely?.currentInsurelyPolicyIndex === index
                  }
                  onPress={() => onChangeAnimal(index)}
                >
                  <FlexRow alignItems="center">
                    <Typography>
                      {item.insurance.insuranceSubType === "dogInsurance"
                        ? "🐶 "
                        : "🐱 "}
                    </Typography>
                    <Gutter size={1} horizontal />
                    {label}
                  </FlexRow>
                </Radio>
                {index < (insurance.insurely?.policies?.length ?? 0) - 1 && (
                  <Gutter size={2} />
                )}
              </Fragment>
            );
          })}
        </ChatMessage>
        <ResponsiveGutter tablet={7} />
        <Gutter size={2} />
        {!isCancellationPossible && (
          <CancellationDisclaimer
            competitorName={getCompetitorName(insurance.insurely) ?? "?"}
          />
        )}
        <FlowButtons
          next={onNext}
          backButton={false}
          disabled={!isValid}
          previous={previous}
          inProgress={transitioning}
          custom={
            <Button
              title={t("global.btn.skip")}
              variant="custom"
              textColor="blue300"
              color="white"
              onPress={() => {
                dispatch(
                  updateInsuranceById(
                    {
                      id: insurance.id,
                      update: {
                        insurely: {
                          ...insurance.insurely,
                          shouldCompare: false,
                        },
                      },
                    },
                    { refetch: false }
                  )
                );

                // Should be better solved with a `navigateToNextOnSuccess` meta prop
                setTimeout(() => {
                  next();
                }, 100);
              }}
            />
          }
        />
      </ChatContainer>
    </>
  );
};

export default ChooseAnimal;
